import React from "react";

import { Button, InfoMessage } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { usePlanGate } from "@/hooks/usePlanGate";
import { usePlans } from "@/hooks/useSubscription";

import { formatPrice } from "@/utils/USubscription";

import { PLAN_NAMES } from "@/constants/CPlans";

import { TFeatureKey } from "@/types/TSubscription";

interface IPlanFeatureBannerProps {
	on: TFeatureKey;
	className?: string;
}

/**
 * At the head of a page whose feature the plan does not include: what
 * it is, which plan has it, from how much, and a button to get it. In
 * the page, not above every page — a locked feature is news where it
 * is missing, noise elsewhere. Nothing when the feature is on.
 */
export const PlanFeatureBanner: React.FC<IPlanFeatureBannerProps> = ({
	on,
	className,
}) => {
	const gate = usePlanGate(on);
	const plans = usePlans(gate.locked && !!gate.requiredPlan);

	if (!gate.locked) return null;

	const required = plans.data?.find(
		(plan) => plan.code === gate.requiredPlan
	);
	const planName = gate.requiredPlan ? PLAN_NAMES[gate.requiredPlan] : "";
	const price =
		required?.priceMonth != null
			? `${formatPrice(required.priceMonth, required.currency)} ${t("subscription.plans.perMonth")}`
			: null;

	return (
		<InfoMessage
			color="info"
			picto="lock"
			className={className}
			data-testid={`plan-feature-banner-${on}`}
		>
			<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
				<span>
					{gate.requiredPlan
						? t(`subscription.feature.${on}`, { plan: planName })
						: t("subscription.gate.unavailable")}
					{price && (
						<span className="text-neutral-500"> — {price}</span>
					)}
				</span>
				{gate.requiredPlan && (
					<Button variant="link" size="sm" onClick={gate.open}>
						{t("subscription.upgrade.cta", { plan: planName })}
					</Button>
				)}
			</span>
		</InfoMessage>
	);
};
